// Shared client for the per-user promo state persisted at /user/promo-state.
// State shape per promo id: { snoozedUntil: ISO string|null, converted: bool, showCount: int }

let statePromise = null;
let slotClaimed = false;

async function fetchState() {
  if (!statePromise) {
    // On failure this stays resolved to null for the rest of the page load:
    // navigation.vue remounts per route, and retrying a failing endpoint on
    // every navigation is worse than showing no promos until a reload.
    statePromise = window.$A.UserService.getPromoState()
      .then((res) => (res && res.promos) || {})
      .catch(() => null);
  }
  return statePromise;
}

export async function getPromoEntry(promoId) {
  const promos = await fetchState();
  if (promos === null) return null; // fetch failed — callers should not show
  return promos[promoId] || { snoozedUntil: null, converted: false, dismissed: false, showCount: 0 };
}

export function isSnoozed(entry) {
  return !!entry.snoozedUntil && new Date(entry.snoozedUntil).getTime() > Date.now();
}

export async function updatePromoEntry(promoId, { snoozeDays, converted, dismissed, incrementShowCount } = {}) {
  const request = { promoId };
  if (snoozeDays) {
    request.snoozedUntil = new Date(Date.now() + snoozeDays * 24 * 60 * 60 * 1000).toISOString();
  }
  if (converted !== undefined) request.converted = converted;
  if (dismissed !== undefined) request.dismissed = dismissed;
  if (incrementShowCount) request.incrementShowCount = true;
  try {
    await window.$A.UserService.updatePromoState(request);
  } catch (e) {
    // non-critical; worst case the promo shows again
  }
}

// Only one promo card may render per session.
export function claimPromoSlot() {
  if (slotClaimed) return false;
  slotClaimed = true;
  return true;
}

// Eligibility gate shared by the proof-count promos: true once the account has
// created at least `minProofs` proofs. Caches a positive result in
// localStorage (eligibleKey) and limits the metrics call to once per session
// while ineligible (checkedKey).
export async function hasCreatedProofs(minProofs, eligibleKey, checkedKey) {
  try {
    if (window.localStorage.getItem(eligibleKey) === "1") return true;
    if (window.sessionStorage.getItem(checkedKey) === "1") return false;
    window.sessionStorage.setItem(checkedKey, "1");
  } catch (e) {
    // storage unavailable; fall through to a live check
  }
  const metrics = await window.$A.AccountService.GetOverviewMetrics(
    new Date(0).toISOString(),
    new Date().toISOString()
  );
  const card = ((metrics && metrics.reportCards) || []).find(
    (c) => c.description === "Total Proofs Created"
  );
  const count = parseFloat((card && card.number) || "0");
  if (!(count >= minProofs)) return false;
  try {
    window.localStorage.setItem(eligibleKey, "1");
  } catch (e) {
    // localStorage unavailable
  }
  return true;
}
